import isEqual from "lodash/isEqual";
import { SimpleEventEmitter } from "@utils/SimpleEventEmitter";
import { toJS } from "mobx";
import { omit } from "@bonliva-crm/common";
import { store } from "@store";
import { createExpertiseInputSimple, FilterManager } from "@search";
import {
  BookingWeekQueryInput,
  CostCenter,
  MyProfile_GetAllowedCostCenters
} from "@graphql";
import { Filter, getInitialFilter } from "./utils";
import { isEmpty } from "lodash";

export class BookingFilterManager extends FilterManager<Filter> {
  static Instance: BookingFilterManager;

  static Create(customInitialFilter: Partial<Filter>) {
    if (!BookingFilterManager.Instance) {
      BookingFilterManager.Instance = new BookingFilterManager(customInitialFilter);
    }

    return BookingFilterManager.Instance;
  }

  fetchEmitter = new SimpleEventEmitter<{ skip?: number; limit?: number }>();
  disabledFields: Array<keyof Filter> = [];
  private hiddenFields: Array<keyof Filter> = [];
  private loadingCostCenters = false;
  private costCentersData: CostCenter[] = [];

  protected constructor(customInitialFilter: Partial<Filter>) {
    super(
      {
        initialFilter: {
          ...getInitialFilter(),
          ...customInitialFilter
        }
      },
      async () => {
        this.fetchEmitter.trigger({});
      }
    );

    this.loadAllowedCostCenters();
  }

  private async loadAllowedCostCenters() {
    if (this.loadingCostCenters) return;
    this.loadingCostCenters = true;

    try {
      const { data } = await MyProfile_GetAllowedCostCenters.func(
        {},
        { fetchPolicy: "network-only" }
      );

      if (data?.me?.allowedCostCenters) {
        this.costCentersData = [...data.me.allowedCostCenters];
      }
    } catch (err) {
      console.error("Failed to load allowed cost centers:", err);
    } finally {
      this.loadingCostCenters = false;
    }
  }

  get costCenters() {
    return this.costCentersData;
  }

  get queryInput(): BookingWeekQueryInput {
    const areasOfExpertise = createExpertiseInputSimple(
      this.filter.areasOfExpertise,
      store().generalSearchStore.areasOfExp
    );

    // a hack for pre-professions2.0 bookings
    if (this.filter.areasOfExpertise.includes("Psykolog")) {
      areasOfExpertise.push("Psykolog");
    }

    const filter: BookingWeekQueryInput = {
      ...omit(
        this.filter,
        "areasOfExpertise",
        "consultants",
        "customers",
        "projectResponsiblePersonId",
        "projectId"
      ),
      areasOfExpertise,
      consultantIds: this.filter.consultants.map((x) => x.value),
      customerIds: this.filter.customers.map((x) => x.value),
      ...this.filterRegionToCountryRegion()
    };

    if (!isEmpty(this.filter.projectId)) {
      filter.projectId = this.filter.projectId;
    }

    if (!isEmpty(this.filter.projectResponsiblePersonId)) {
      filter.projectResponsiblePersonId = this.filter.projectResponsiblePersonId;
      filter.projectId = [];
    }

    // TODO: implement group filter if required for booking
    // filter.group = toGroup(filter.customerType);
    // filter.customerType = toType(filter.customerType);
    return filter;
  }

  get syncReportQueryInput(): BookingWeekQueryInput {
    const areasOfExpertise = createExpertiseInputSimple(
      this.filter.areasOfExpertise,
      store().generalSearchStore.areasOfExp
    );

    // a hack for pre-professions2.0 bookings
    if (this.filter.areasOfExpertise.includes("Psykolog")) {
      areasOfExpertise.push("Psykolog");
    }

    const filter: BookingWeekQueryInput = {
      ...omit(this.filter, "areasOfExpertise", "consultants", "customers"),
      areasOfExpertise,
      consultantIds: this.filter.consultants.map((x) => x.value),
      customerIds: this.filter.customers.map((x) => x.value),
      ...this.filterRegionToCountryRegion()
    };

    if (!isEmpty(this.filter.projectResponsiblePersonId)) {
      filter.projectResponsiblePersonId = this.filter.projectResponsiblePersonId;
      filter.projectId = [];
    } else if (!isEmpty(this.filter.projectId)) {
      filter.projectId = this.filter.projectId;
    }

    return filter;
  }

  isFilterDirty(key: keyof Filter): boolean {
    const keysDeep: Array<keyof Filter> = [
      "areasOfExpertise",
      "responsiblePersonId",
      "specialization"
    ];

    if (keysDeep.includes(key)) {
      return !isEqual(toJS(this.filter[key]), toJS(this._initialFilter[key]));
    }

    if (key === "from" || key === "to") {
      if (this.filter[key] !== null) {
        return this.filter[key].valueOf() !== this._initialFilter[key].valueOf();
      } else {
        return true;
      }
    }

    return super.isFilterDirty(key);
  }

  isFilterDisabled(key: keyof Filter): boolean {
    return this.disabledFields.includes(key);
  }

  renderField = (fieldName: keyof Filter, cb: () => React.ReactNode) => {
    return !this.hiddenFields.includes(fieldName) ? cb() : null;
  };

  hideFields(fields: Array<keyof Filter>) {
    this.hiddenFields = fields;
  }

  unhideFields() {
    this.hiddenFields = [];
  }

  updateFilter(value: Partial<Filter>, debounced = false, asInitial = false) {
    super.updateFilter(value, debounced);

    if (asInitial) {
      this._initialFilter = { ...this._initialFilter, ...value };
    }
  }

  private filterRegionToCountryRegion(): {
    country: string[];
    region: string[];
  } {
    // transforms an array of "country:region"
    const [country, region] = (this.filter.region || [])
      .map((x) => x.split(":"))
      .reduce(
        (p, c) => {
          p[0].push(c[0]);
          p[1].push(c[1]);
          return p;
        },
        [[], []]
      )
      .map((x) => x.unique())
      .map((x) => x.filter((y) => y !== "*"));

    return { country, region };
  }
}
